import {
    QueryClient,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult
} from '@tanstack/react-query';
import { BudgetModel } from 'src/types/budget.model';
import BudgetService, { BudgetUpdate } from 'src/services/budget.service';
import { BudgetSummaryModel } from 'src/types/budget-summary.model';

const budgetsQueryKey: string = 'budgets';
const budgetsSummaryQueryKey: string = 'budgetsSummary';
const defaultBudgetsQueryKey: string = 'defaultBudgets';

export const useBudget = (groupId: string): UseQueryResult<BudgetModel> => {
    return useQuery<BudgetModel>({
        queryKey: [budgetsQueryKey, groupId],
        queryFn: async () => await BudgetService.getABudget(groupId),
        enabled: !!groupId
    });
};

export const useBudgets = (): UseQueryResult<BudgetModel[]> => {
    return useQuery<BudgetModel[]>({
        queryKey: [budgetsQueryKey],
        queryFn: async () => await BudgetService.getBudget()
    });
};

export const useBudgetsSummary = (): UseQueryResult<BudgetSummaryModel> => {
    return useQuery<BudgetSummaryModel>({
        queryKey: [budgetsSummaryQueryKey],
        queryFn: async () => await BudgetService.getBudgetSummary()
    });
};

export const useDefaultBudgets = (): UseQueryResult<BudgetModel[]> => {
    return useQuery<BudgetModel[]>({
        queryKey: [defaultBudgetsQueryKey],
        queryFn: async () => await BudgetService.getSuggested()
    });
};

export const useUpdateBudget = (): UseMutationResult<BudgetModel, unknown, BudgetModel> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (budget: BudgetModel) => await BudgetService.updateBudget(budget),
        onSuccess: (): void => {
            void invalidateBudgets(queryClient);
        }
    });
};

export const useUpdateBudgets = (): UseMutationResult<void, unknown, BudgetUpdate[]> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (budgets: BudgetUpdate[]) => await BudgetService.updateBudgets(budgets),
        onSuccess: (): void => {
            void invalidateBudgets(queryClient);
        }
    });
};

export const useDeleteBudget = (): UseMutationResult<void, unknown, BudgetModel> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (budget: BudgetModel) => await BudgetService.deleteBudget(budget),
        onSuccess: (): void => {
            void invalidateBudgets(queryClient);
        }
    });
};

export const invalidateBudgets = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries([budgetsQueryKey]);
    await queryClient.invalidateQueries([budgetsSummaryQueryKey]);
};
